import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Typography } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import { BaseButton } from 'components/BaseButton';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ImportPage } from 'services/imports/fileToPages';
import type { AmendmentSuggestion } from 'services/imports/reviewAmendments';

type AmendmentRow = AmendmentSuggestion & { current: string | null };

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  key: {
    fontWeight: 500,
    wordBreak: 'break-word',
  },
  value: {
    wordBreak: 'break-word',
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    margin: -1,
    padding: 0,
    border: 0,
  },
}));

type AmendmentsReviewProps = {
  rows: readonly AmendmentRow[];
  pages: readonly ImportPage[];
  disabled: boolean;
  onAccept: (id: string) => void;
  onDismiss: (id: string) => void;
};

export const AmendmentsReview = ({ rows, pages, disabled, onAccept, onDismiss }: AmendmentsReviewProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  const announcement = rows.length > 0 ? t('imports:amendments.announce', { count: rows.length }) : '';
  const pageById = new Map(pages.map((page) => [page.id, page]));
  const sourceLabel = (row: AmendmentRow): string => {
    const page = pageById.get(row.pageId);
    const where = page ? `${page.fileName} - ${t('imports:viewer.page', { page: page.pageNumber })}` : '';
    const line = row.src ? t('imports:amendments.line', { line: row.src.line.replace(/^L/, '') }) : t('imports:amendments.noSource');
    return where === '' ? line : `${where}, ${line}`;
  };

  return (
    <>
      <div role="status" aria-live="polite" className={classes.srOnly}>{announcement}</div>
      {rows.length > 0 && (
        <div className={classes.root} aria-label={t('imports:amendments.title')}>
          <Typography variant="subtitle2">{t('imports:amendments.title')}</Typography>
          <Typography variant="caption" className={classes.muted}>{t('imports:amendments.hint')}</Typography>
          {rows.map((row) => (
            <div key={row.id} className={classes.row}>
              <Typography variant="body2" className={classes.key}>{row.key}</Typography>
              <Typography variant="caption" className={classes.muted}>{sourceLabel(row)}</Typography>
              {row.current !== null && (
              <Typography variant="body2" className={classes.muted}>
                {`${t('imports:amendments.current')}: ${row.current}`}
              </Typography>
              )}
              <Typography variant="body2" className={classes.value}>
                {`${t('imports:amendments.suggested')}: ${row.value}`}
              </Typography>
              <div className={classes.actions}>
                <BaseButton
                  text={t('imports:amendments.dismiss')}
                  icon={faTimes}
                  buttonVariant="text"
                  buttonColor="inherit"
                  textColor={disabled ? theme.palette.text.disabled : theme.palette.text.primary}
                  height={theme.spacing(4)}
                  disabled={disabled}
                  onClick={() => onDismiss(row.id)}
                />
                <BaseButton
                  text={t('imports:amendments.accept')}
                  icon={faCheck}
                  buttonVariant="outlined"
                  buttonColor="inherit"
                  textColor={disabled ? theme.palette.text.disabled : theme.palette.primary.main}
                  height={theme.spacing(4)}
                  disabled={disabled}
                  onClick={() => onAccept(row.id)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};
